/**
 * "No restriction" as a stable module-level identity.
 *
 * Every unrestricted render — the default case, since the allowlist is only resolved for a known
 * minor — must hand DocumentTypeField the *same* array. A fresh `[]` literal per render invalidates
 * its `visibleItems` → `options` memo chain and re-renders the select for every passenger and every
 * document slot.
 */
export const NO_DOCUMENT_RESTRICTION = [];

/**
 * Narrows an allowlist value to something safe to filter with.
 *
 * Anything that is not a non-empty array of non-empty strings means "not configured", which imposes
 * no restriction. The per-entry check matters as much as the `Array.isArray` one: a value like
 * `['TI', 3]` is an array, and treating it as a restriction would narrow the picker to `TI` on the
 * strength of a malformed row — the opposite of the documented fail-open behaviour.
 *
 * @param {*} value - The configured value, from anywhere.
 * @returns {Array<string>} The allowlist, or the shared empty identity.
 */
export const normalizeAllowedDocumentTypes = (value) => {
  if (!Array.isArray(value) || value.length === 0) return NO_DOCUMENT_RESTRICTION;

  return value.every((entry) => typeof entry === 'string' && entry)
    ? value
    : NO_DOCUMENT_RESTRICTION;
};

/**
 * The document types a passenger under the tenant's adult age may hold, as published by
 * platform-purchase in `whitelabelConfig.features.MINOR_ALLOWED_DOCUMENT_TYPES`.
 *
 * Normalised here rather than at each call site. A remote configuration row can arrive as `null`
 * (React `defaultProps` only cover `undefined`, so `null` reached `.length` and blanked the
 * passengers step) or as a comma-joined string, which is truthy and whose `.includes` matches by
 * substring — filtering would then go quietly wrong. Anything that is not an array of strings means
 * "not configured", which imposes no restriction: the same default every tenant that has not opted
 * in keeps.
 *
 * @param {Object} features - The tenant's `whitelabelConfig.features`.
 * @returns {Array<string>} The configured allowlist, or an empty array.
 */
export const getMinorAllowedDocumentTypes = (features) =>
  normalizeAllowedDocumentTypes(features?.MINOR_ALLOWED_DOCUMENT_TYPES);

/**
 * The age Colombia issues a Cédula de Ciudadanía at, and the upper bound of the minor allowlist.
 */
export const ADULT_AGE = 18;

/**
 * The age Colombia issues a Tarjeta de Identidad at, so 6 is the last year a child has nothing but
 * a Registro Civil (or a passport).
 *
 * This boundary lives in code rather than in configuration on purpose. The carrier's DISCOUNT bands
 * move — they have said so, and next year "Niños" may end at 10 or at 5 — but the age a document is
 * ISSUED at is Colombian law and does not. The two are independent, and only the discount side
 * reads a band from the provider.
 */
export const YOUNG_CHILD_MAX_AGE = 6;

/**
 * Caches the narrowed young-child list per `features` object.
 *
 * Two reasons, and both matter. `DocumentTypeField` memoizes on this array, so a fresh one per
 * render would invalidate its chain for every passenger × every document slot. And the warning
 * below has to fire once per configuration rather than once per render.
 *
 * A `WeakMap` so a replaced `features` object is collected with its entry.
 */
const narrowedYoungChildLists = new WeakMap();

/**
 * Narrows the young-child list to what platform-purchase will actually accept.
 *
 * Compared by exact value rather than through `getDocumentTypeAliases`. The alias problem is real
 * but it is a different comparison: it exists because a config row is written in PROCESSOR values
 * while the select works in BRAND values. Both rows here come from the same config system and the
 * same operator, so they share a vocabulary, and pulling the alias helper in would make this module
 * depend on the Redux store it deliberately does not touch.
 *
 * @param {Array<string>} configured - The young-child row.
 * @param {Array<string>} minor - The minor row, which platform-purchase validates against.
 * @returns {Array<string>} The configured row when nothing was dropped, else the narrowed list.
 */
const narrowToMinorAllowlist = (configured, minor) => {
  const accepted = new Set(minor);
  const kept = configured.filter((type) => accepted.has(type));

  // Identity matters more than a new array: the ordinary, correctly configured case must hand back
  // the very array it was given.
  if (kept.length === configured.length) return configured;

  const dropped = configured.filter((type) => !accepted.has(type));

  if (kept.length === 0) {
    // Narrowing to nothing would read as "no restriction" downstream, which is the opposite of what
    // the misconfiguration calls for. The minor row is the gate's own answer: never wider than it.
    // eslint-disable-next-line no-console
    console.warn(
      `[documentTypes] YOUNG_CHILD_ALLOWED_DOCUMENT_TYPES (${configured.join(
        ', ',
      )}) shares nothing ` +
        `with MINOR_ALLOWED_DOCUMENT_TYPES (${minor.join(
          ', ',
        )}). platform-purchase validates 0-6 ` +
        `passengers against the latter and would answer 422 for every option offered, so the picker ` +
        `falls back to it. Align the two rows.`,
    );

    return minor;
  }

  // eslint-disable-next-line no-console
  console.warn(
    `[documentTypes] YOUNG_CHILD_ALLOWED_DOCUMENT_TYPES offers ${dropped.join(', ')} to a 0-` +
      `${YOUNG_CHILD_MAX_AGE} passenger, but MINOR_ALLOWED_DOCUMENT_TYPES does not accept it. ` +
      `platform-purchase would answer 422 at the passenger save, so the picker drops it. Align the ` +
      `two rows.`,
  );

  return kept;
};

/**
 * The document types a passenger 0–6 may hold, from
 * `whitelabelConfig.features.YOUNG_CHILD_ALLOWED_DOCUMENT_TYPES`.
 *
 * Unconfigured falls back to the flat minor allowlist, which is what every tenant gets today, so
 * adding the key is opt-in and removing it is a complete rollback.
 *
 * **Narrowed to `MINOR_ALLOWED_DOCUMENT_TYPES` when that row is configured.** A 0-6 passenger is a
 * minor, so platform-purchase validates their document against the minor row whatever this one
 * says. Offering a type that row omits would send the buyer to a 422 they cannot act on, on a
 * document the funnel itself proposed. The funnel holds that row — it is the same
 * `features` entry `getMinorAllowedDocumentTypes` reads — so this is enforceable here rather than
 * only documented, and a mismatch is reported once per configuration.
 *
 * An unconfigured minor row imposes no restriction and therefore gates nothing, so there is no
 * ceiling to narrow against and the young-child row stands alone.
 *
 * @param {Object} features - The tenant's `whitelabelConfig.features`.
 * @returns {Array<string>} The configured allowlist narrowed to the minor one, or the minor one.
 */
export const getYoungChildAllowedDocumentTypes = (features) => {
  const configured = normalizeAllowedDocumentTypes(features?.YOUNG_CHILD_ALLOWED_DOCUMENT_TYPES);
  const minor = getMinorAllowedDocumentTypes(features);

  if (configured.length === 0) return minor;
  if (minor.length === 0) return configured;

  if (narrowedYoungChildLists.has(features)) return narrowedYoungChildLists.get(features);

  const narrowed = narrowToMinorAllowlist(configured, minor);
  narrowedYoungChildLists.set(features, narrowed);

  return narrowed;
};

/**
 * The document types an adult may hold, from `whitelabelConfig.features.ADULT_ALLOWED_DOCUMENT_TYPES`.
 *
 * Unconfigured means no restriction, which is what adults have always had here. There is no fallback
 * to the minor allowlist: that list exists to EXCLUDE the Cédula, and handing it to an adult would
 * remove the one document most of them carry.
 *
 * @param {Object} features - The tenant's `whitelabelConfig.features`.
 * @returns {Array<string>} The configured allowlist, or the shared empty identity.
 */
export const getAdultAllowedDocumentTypes = (features) =>
  normalizeAllowedDocumentTypes(features?.ADULT_ALLOWED_DOCUMENT_TYPES);

/**
 * The document types a passenger of this age may hold.
 *
 * Three flat lists rather than one table, because the shape a whitelabel row can actually carry is
 * an array of strings: `content_type` is one of string / integer / boolean / array, and an array of
 * objects would have to travel as a JSON string whose parse errors `cast_array` swallows into `[]`.
 * Flat lists also keep 7–17 on `MINOR_ALLOWED_DOCUMENT_TYPES` — the very row platform-purchase
 * validates against — so for those ages the picker and the gate are the same datum and cannot drift.
 *
 *   0–6    YOUNG_CHILD_ALLOWED_DOCUMENT_TYPES, falling back to the minor list
 *   7–17   MINOR_ALLOWED_DOCUMENT_TYPES
 *   18+    ADULT_ALLOWED_DOCUMENT_TYPES, unrestricted when unset
 *
 * An age we cannot read leaves the picker unrestricted. A negative age means a future birth date,
 * from which nothing can be concluded; a missing one is the ordinary case before the buyer types it.
 *
 * Every branch returns a shared or configured array by reference, so the same features object and
 * the same age hand `DocumentTypeField` the same identity and do not re-render the select.
 *
 * @param {Object} features - The tenant's `whitelabelConfig.features`.
 * @param {number} age - The passenger's derived age.
 * @returns {Array<string>} The allowed types, or the shared empty identity.
 */
export const getDocumentTypesForAge = (features, age) => {
  if (typeof age !== 'number' || !Number.isFinite(age) || age < 0) return NO_DOCUMENT_RESTRICTION;

  if (age <= YOUNG_CHILD_MAX_AGE) return getYoungChildAllowedDocumentTypes(features);
  if (age < ADULT_AGE) return getMinorAllowedDocumentTypes(features);

  return getAdultAllowedDocumentTypes(features);
};

/**
 * The narrowest claim available about a passenger known only to be an infant.
 *
 * `LapChildField` has no date of birth to evaluate — a lap child is a minor by definition — so it
 * takes the youngest list. This is why that list covers 0–6 rather than starting at the age the
 * carrier's discount opens.
 *
 * @param {Object} features - The tenant's `whitelabelConfig.features`.
 * @returns {Array<string>} The youngest allowlist.
 */
export const getYoungestDocumentTypeBand = (features) =>
  getYoungChildAllowedDocumentTypes(features);

export default getMinorAllowedDocumentTypes;
